import React, { useState, useMemo } from 'react';
import { ClipboardCheck, CheckCircle, Plus, AlertTriangle } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import { useOrganizationStore } from '../stores/organizationStore';
import { useStatistics } from '../hooks/useStatistics';
import { Header } from '../components/layout/Header';
import { Button, ErrorDisplay } from '../components/common';
import { 
  StatisticsCard, 
  StatisticsBarChart, 
  UserStatisticsTable, 
  DateRangePicker 
} from '../components/statistics';
import { useTranslation } from 'react-i18next';

type PeriodType = '7d' | '30d' | '90d' | 'custom';

const StatisticsPage: React.FC = () => {
  const { user } = useAuthStore();
  const { t } = useTranslation();
  const [period, setPeriod] = useState<PeriodType>('30d');
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');
  const { activeOrganizationId } = useOrganizationStore();

  // Only admins in the current organization can access this page
  const isAdmin = user?.organizationRoles?.find(r => r.orgId === activeOrganizationId)?.role === 'ADMIN';

  // Build query options
  const statsOptions = useMemo(() => {
    if (period === 'custom') {
      return {
        period: 'custom' as const,
        startDate: customStartDate,
        endDate: customEndDate
      };
    }
    return { period };
  }, [period, customStartDate, customEndDate]);

  const { data, loading, error, refetch } = useStatistics(statsOptions);

  const handlePeriodChange = (newPeriod: PeriodType) => {
    setPeriod(newPeriod);
  };

  // Show access denied for non-admins
  if (!isAdmin) {
    return (
      <div className="flex flex-col h-full">
        <Header title={t('statistics.title')} />
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 sm:p-6 text-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-red-700 dark:text-red-400 mb-2">
              {t('statistics.accessDenied')}
            </h2>
            <p className="text-red-600 dark:text-red-300">
              {t('statistics.accessDeniedMessage')}
            </p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <Header title={t('statistics.title')} />
      
      <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8">
          <p className="text-gray-500 dark:text-gray-400">
            {t('statistics.overview')}
          </p>
        <DateRangePicker
          period={period}
          startDate={customStartDate}
          endDate={customEndDate}
          onPeriodChange={handlePeriodChange}
          onStartDateChange={setCustomStartDate}
          onEndDateChange={setCustomEndDate}
        />
      </div>

      {/* Error State */}
      {error && (
        <div className="mb-6">
          <ErrorDisplay
            message={error}
            variant="error"
            dismissible
            onDismiss={() => refetch()}
          />
          <Button variant="secondary" onClick={() => refetch()} className="mt-2">{t('statistics.retry')}</Button>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="space-y-6">
          {/* Skeleton Stat Cards - mimic the stat cards with icon, value, and trend */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-3">
                  <div className="animate-pulse bg-gray-200 dark:bg-gray-700 rounded h-3 w-24" />
                  <div className="animate-pulse bg-gray-200 dark:bg-gray-700 rounded-md w-5 h-5" />
                </div>
                <div className="animate-pulse bg-gray-200 dark:bg-gray-700 rounded h-8 w-16 mb-2" />
                <div className="animate-pulse bg-gray-200 dark:bg-gray-700 rounded h-3 w-20" />
              </div>
            ))}
          </div>
          {/* Skeleton Chart Area - mimic a chart with axes and data points */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-6">
              <div className="animate-pulse bg-gray-200 dark:bg-gray-700 rounded h-5 w-32" />
              <div className="animate-pulse bg-gray-200 dark:bg-gray-700 rounded h-8 w-24" />
            </div>
            <div className="animate-pulse bg-gray-100 dark:bg-gray-700/50 rounded-lg h-64" />
          </div>
          {/* Skeleton Table - mimic a data table with header and rows */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="animate-pulse bg-gray-200 dark:bg-gray-700 rounded h-5 w-40" />
            </div>
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="p-4 flex gap-4">
                  <div className="animate-pulse bg-gray-200 dark:bg-gray-700 rounded h-4 w-1/4" />
                  <div className="animate-pulse bg-gray-200 dark:bg-gray-700 rounded h-4 w-1/4" />
                  <div className="animate-pulse bg-gray-200 dark:bg-gray-700 rounded h-4 w-1/4" />
                  <div className="animate-pulse bg-gray-200 dark:bg-gray-700 rounded h-4 w-1/4" />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      {!loading && data && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatisticsCard
              title={t('statistics.ui.attendanceDays')}
              value={data.summary.totalAttendanceDays}
              icon={<ClipboardCheck className="w-5 h-5" />}
              trend={data.trend.attendanceChange}
              color="blue"
            />
            <StatisticsCard
              title={t('statistics.ui.tasksCompleted')}
              value={data.summary.totalTasksCompleted}
              icon={<CheckCircle className="w-5 h-5" />}
              trend={data.trend.completionChange}
              color="green"
            />
            <StatisticsCard
              title={t('statistics.ui.tasksCreated')}
              value={data.summary.totalTasksCreated}
              icon={<Plus className="w-5 h-5" />}
              trend={data.trend.creationChange}
              color="yellow"
            />
            <StatisticsCard
              title={t('statistics.ui.overdueTasks')}
              value={data.summary.totalOverdueTasks}
              icon={<AlertTriangle className="w-5 h-5" />}
              trend={data.trend.overdueChange}
              color="red"
            />
          </div>

          {/* Average Completion Rate */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {t('statistics.averageCompletionRate')}
              </h3>
              <span className={`text-2xl font-bold ${
                data.summary.averageCompletionRate >= 80 ? 'text-green-500' :
                data.summary.averageCompletionRate >= 50 ? 'text-yellow-500' :
                'text-red-500'
              }`}>
                {data.summary.averageCompletionRate}%
              </span>
            </div>
            <div className="w-full h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div 
                className={`h-full rounded-full transition-all duration-500 ${
                  data.summary.averageCompletionRate >= 80 ? 'bg-green-500' :
                  data.summary.averageCompletionRate >= 50 ? 'bg-yellow-500' :
                  'bg-red-500'
                }`}
                style={{ width: `${data.summary.averageCompletionRate}%` }}
              />
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
              {t('statistics.basedOnUsers', { count: data.summary.totalUsers })}
            </p>
          </div>

          {/* Daily Breakdown Chart */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              {t('statistics.dailyActivity')}
            </h3>
            <StatisticsBarChart 
              data={data.dailyBreakdown}
              height={300}
            />
          </div>

          {/* User Statistics Table */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              {t('statistics.userStatistics')}
            </h3>
            <UserStatisticsTable users={data.byUser} />
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && !data && (
        <div className="text-center py-12">
          <p className="text-gray-500 dark:text-gray-400">{t('statistics.noData')}</p>
        </div>
      )}
      </main>
    </div>
  );
};

export default StatisticsPage;
